import React, { useState, useEffect, useRef } from "react";
import {
  ArrowLeft,
  Clock,
  Star,
  CheckCircle,
  XCircle,
  Trophy,
  Coins,
  BookOpen,
  AlertCircle,
  RefreshCw,
  FileText,
  Target,
  Award,
  Brain,
  TrendingUp,
  Zap,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Edit3,
  BarChart3,
  Shield,
  Crown,
  Flame,
  AlertTriangle,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import "../styles/rankedGame.css";
import { API_BASE, DEV_MODE } from "../config";
import levelsData from "../data/levels.json";
import apiFetch from "../apiFetch";

const TIER_ICONS = { BookOpen, Target, Award, Star, Crown, Brain, Flame, Zap };

const cacheKey = (tier) => `ranked_cache_tier_${tier}`;

const getCachedGameData = (tier) => {
  try {
    const raw = localStorage.getItem(cacheKey(tier));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setCachedGameData = (tier, data) => {
  try {
    localStorage.setItem(cacheKey(tier), JSON.stringify(data));
  } catch {
    // storage quota exceeded — proceed without caching
  }
};

const clearCachedGameData = (tier) => {
  localStorage.removeItem(cacheKey(tier));
};

const RankedGame = () => {
  const navigate = useNavigate();
  // Game state management
  const [gameState, setGameState] = useState("lobby"); // lobby, loading, playing, reviewing, results
  const [currentElo, setCurrentElo] = useState(150); // Starting ELO   ------ELO TO SELECT
  const [passage, setPassage] = useState("");
  const [mcqQuestions, setMcqQuestions] = useState([]);
  const [writtenQuestions, setWrittenQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [mcqAnswers, setMcqAnswers] = useState({});
  const [writtenAnswers, setWrittenAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(900); // 15 minutes
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [markingResults, setMarkingResults] = useState(null);
  const [showPassage, setShowPassage] = useState(true);
  const [reviewMode, setReviewMode] = useState(false);
  const [currentReviewQuestion, setCurrentReviewQuestion] = useState(0);
  const [newElo, setNewElo] = useState(null);
  const [eloChange, setEloChange] = useState(0);
  const [coinsEarned, setCoinsEarned] = useState(0);
  // Ranked ELO history (for future charts)
  const [eloHistory, setEloHistory] = useState([]); // raw rows from API
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const submitModalRef = useRef(null);
  const [eloSeries, setEloSeries] = useState([]); // [{ x: Date, y: number }]

  useEffect(() => {
    if (!showSubmitModal) return;
    submitModalRef.current?.querySelector("button")?.focus();
    const handler = (e) => { if (e.key === "Escape") setShowSubmitModal(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showSubmitModal]);

  // Get tier based on ELO
  const getTier = (elo) => {
    if (elo < 100) return 1;
    if (elo < 200) return 2;
    if (elo < 300) return 3;
    if (elo < 400) return 4;
    if (elo < 500) return 5;
    if (elo < 600) return 6;
    if (elo < 700) return 7;
    if (elo < 800) return 8;
    if (elo < 900) return 9;
    if (elo < 1000) return 10;
    if (elo < 1100) return 11;
    if (elo < 1200) return 12;
    if (elo < 1300) return 13;
    if (elo < 1400) return 14;
    if (elo < 1500) return 15;
    if (elo < 1600) return 16;
    if (elo < 1700) return 17;
    if (elo < 1800) return 18;
    if (elo < 1900) return 19;
    if (elo < 2000) return 20;
    if (elo < 2100) return 21;
    if (elo < 2200) return 22;
    if (elo < 2300) return 23;
    if (elo < 2400) return 24;
    if (elo < 2500) return 25;
    if (elo < 2600) return 26;
    if (elo < 2700) return 27;
    if (elo < 2800) return 28;
    if (elo < 2900) return 29;
    return 30;
  };

  const getTierInfo = (tier) => {
    const data = levelsData.tiers[String(tier)] || levelsData.tiers["1"];
    const IconComponent = TIER_ICONS[data.icon] || BookOpen;
    return { ...data, icon: <IconComponent /> };
  };

  // Calculate ELO change based on score
  const calculateEloChange = (score) => {
    const percentage = (score / 20) * 100;
    if (percentage >= 95) return 50;
    if (percentage >= 90) return 40;
    if (percentage >= 85) return 30;
    if (percentage >= 80) return 25;
    if (percentage >= 75) return 20;
    if (percentage >= 70) return 15;
    if (percentage >= 65) return 10;
    if (percentage >= 60) return 5;
    if (percentage >= 55) return 0;
    if (percentage >= 50) return -5;
    if (percentage >= 45) return -10;
    if (percentage >= 40) return -15;
    if (percentage >= 35) return -20;
    if (percentage >= 30) return -25;
    return -30;
  };

  const fetchRankedEloHistory = async (order = "asc", limit = 200) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId) return;

    try {
      const res = await apiFetch(
        `/api/ranked-elo/history?userId=${userId}&order=${order}&limit=${limit}`,
      );
      if (!res.ok) {
        console.error("❌ Failed to fetch ELO history:", await res.text());
        return;
      }
      const rows = await res.json();
      setEloHistory(rows);

      // prep a simple series for chart libs later
      const series = rows.map((r) => ({
        x: new Date(r.played_at),
        y: r.elo_after_match,
      }));
      setEloSeries(series);
    } catch (err) {
      console.error("❌ Error fetching ELO history:", err);
    }
  };

  // Load ranked ELO from backend when the lobby first shows--
  useEffect(() => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId) return;

    (async () => {
      try {
        const res = await apiFetch(
          `/api/ranked-elo?userId=${userId}`,
        );
        if (res.ok) {
          const data = await res.json();
          setCurrentElo(data.elo ?? 150);
        } else {
          console.error("❌ Failed to fetch ranked ELO:", await res.text());
        }
      } catch (err) {
        console.error("❌ Error fetching ranked ELO:", err);
      }

      // history (ascending so x-axis is left→right)
      await fetchRankedEloHistory("asc", 200);
    })();
  }, []);
  //--

  // Timer effect
  useEffect(() => {
    if (DEV_MODE) return;
    if (gameState === "playing" && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && gameState === "playing") {
      handleSubmit();
    }
  }, [timeLeft, gameState]);

  const startGame = () => {
    const tier = getTier(currentElo);
    initializeGame(tier);
  };

  const initializeGame = async (tier) => {
    setGameState("loading");
    setLoading(true);
    setError("");

    try {
      let data = getCachedGameData(tier);

      if (!data) {
        const response = await apiFetch(`/api/generate-ranked`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tier }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        data = await response.json();

        if (!data.passage || !data.mcqQuestions || !data.writtenQuestions) {
          throw new Error("Invalid data received from API");
        }

        setCachedGameData(tier, data);
      }

      setPassage(data.passage);
      setMcqQuestions(data.mcqQuestions);
      setWrittenQuestions(data.writtenQuestions);

      // Initialize answers
      const initialMcqAnswers = {};
      data.mcqQuestions.forEach((_, index) => {
        initialMcqAnswers[index] = "";
      });
      setMcqAnswers(initialMcqAnswers);

      const initialWrittenAnswers = {};
      data.writtenQuestions.forEach((_, index) => {
        initialWrittenAnswers[index] = "";
      });
      setWrittenAnswers(initialWrittenAnswers);

      setGameState("playing");
      setTimeLeft(900); // 15 minutes
      setCurrentQuestion(0);
    } catch (error) {
      console.error("Error fetching game data:", error);
      navigate("/error");
    } finally {
      setLoading(false);
    }
  };

  const handleMcqAnswerChange = (questionIndex, answer) => {
    setMcqAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));
  };

  const handleWrittenAnswerChange = (questionIndex, answer) => {
    setWrittenAnswers((prev) => ({
      ...prev,
      [questionIndex]: answer,
    }));
  };

  const handleSubmit = async (isExplicit = false) => {
    setLoading(true);
    setGameState("reviewing");

    try {
      const tier = getTier(currentElo);
      const response = await apiFetch(`/api/mark-ranked`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tier: tier,
          mcqQuestions: mcqQuestions,
          writtenQuestions: writtenQuestions,
          mcqAnswers: mcqAnswers,
          writtenAnswers: writtenAnswers,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const results = await response.json();
      setMarkingResults(results);

      // Calculate coins first
      const calculateCoinsEarned = (score, elo) => {
        const baseMultiplier = 1;
        const eloFactor = elo / 10;
        const performanceFactor = score / 20;
        return Math.floor(baseMultiplier * eloFactor * performanceFactor);
      };

      const addCoinsAndXPToUser = async (coinsToAdd) => {
        const userId = parseInt(localStorage.getItem("userId"), 10);
        if (!userId || coinsToAdd <= 0) return;

        try {
          // 1) Add coins
          const response = await apiFetch(
            `/api/resources/update-coins`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ userId, delta: coinsToAdd }),
            },
          );
          if (!response.ok) {
            console.error("❌ Failed to update coins:", await response.text());
            return;
          }

          // 2) Add XP (coins × 5)
          const xpRes = await apiFetch(
            `/api/resources/xp/add`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ userId, delta: coinsToAdd * 5 }),
            },
          );
          if (!xpRes.ok) {
            console.error("❌ Failed to update XP:", await xpRes.text());
            return;
          }
        } catch (error) {
          console.error("❌ Error updating coins/XP:", error);
        }
      };

      const coins = calculateCoinsEarned(results.totalScore, currentElo);
      setCoinsEarned(coins);

      // Always calculate ELO change for display
      const change = calculateEloChange(results.totalScore);
      const newEloValue = Math.max(1, currentElo + change);
      setEloChange(change);
      setNewElo(newEloValue);

      if (isExplicit) {
        addCoinsAndXPToUser(coins);
        setCurrentElo(newEloValue);

        const saveRankedElo = async () => {
          const userId = parseInt(localStorage.getItem("userId"), 10);
          if (!userId) return;
          try {
            const res = await apiFetch(`/api/ranked-elo/update`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                userId,
                newElo: newEloValue,
                delta: change,
                scoreOutOf20: results.totalScore,
                timeUsedSeconds: 900 - timeLeft,
              }),
            });
            if (!res.ok) {
              console.error("❌ Failed to save ranked ELO:", await res.text());
            }
          } catch (err) {
            console.error("❌ Error saving ranked ELO:", err);
          }
        };

        await saveRankedElo();
        await fetchRankedEloHistory("asc", 200);
        clearCachedGameData(getTier(currentElo));
      }

      // Save result to history
      const userId = parseInt(localStorage.getItem("userId"), 10);
      if (userId) {
        apiFetch(`/api/results/save`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId,
            gameType: "ranked",
            title: `Ranked — Tier ${getTier(currentElo)}`,
            score: results.totalScore ?? 0,
            maxScore: 20,
            resultJson: { tier: getTier(currentElo), mcqQuestions, writtenQuestions, mcqAnswers, writtenAnswers, markingResults: results, eloChange: change },
          }),
        }).catch(() => {});
      }

      // Show results
      setGameState("results");
    } catch (error) {
      console.error("Error marking answers:", error);
      setError("Failed to mark answers. Please try again.");
      setGameState("playing");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getTotalQuestions = () => mcqQuestions.length + writtenQuestions.length;
  const getAnsweredCount = () => {
    const mcqCount = Object.values(mcqAnswers).filter((a) => a).length;
    const writtenCount = Object.values(writtenAnswers).filter((a) => a).length;
    return mcqCount + writtenCount;
  };

  const navigateToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  const nextQuestion = () => {
    if (currentQuestion < getTotalQuestions() - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const startReview = () => {
    setReviewMode(true);
    setCurrentReviewQuestion(0);
  };

  const exitReview = () => {
    setReviewMode(false);
  };

  const getCurrentQuestionData = () => {
    if (currentQuestion < mcqQuestions.length) {
      return {
        type: "mcq",
        question: mcqQuestions[currentQuestion],
        answer: mcqAnswers[currentQuestion] || "",
        index: currentQuestion,
      };
    } else {
      const writtenIndex = currentQuestion - mcqQuestions.length;
      return {
        type: "written",
        question: writtenQuestions[writtenIndex],
        answer: writtenAnswers[writtenIndex] || "",
        index: writtenIndex,
      };
    }
  };

  // Lobby Screen
  if (gameState === "lobby") {
    const currentTier = getTier(currentElo);
    const tierInfo = getTierInfo(currentTier);

    return (
      <div className="ranked-lobby">
        <div className="lobby-content">
          <div className="lobby-header">
            <Trophy className="main-icon" />
            <h1>Ranked Reading Challenge</h1>
            <p>Test your skills in competitive reading comprehension</p>
          </div>

          <div className="current-rank">
            <div className="rank-display">
              <div className="rank-icon" style={{ color: tierInfo.color }}>
                {tierInfo.icon}
              </div>
              <div className="rank-info">
                <h2>{tierInfo.name}</h2>
                <p>{tierInfo.description}</p>
                <div className="elo-display">
                  <BarChart3 className="elo-icon" />
                  <span className="elo-value">{currentElo} ELO</span>
                  <span className="tier-label">Tier {currentTier}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="game-info">
            <div className="info-item">
              <Target className="info-icon" />
              <span>6 MCQ + 4 Written Questions</span>
            </div>
            <div className="info-item">
              <Clock className="info-icon" />
              <span>15 Minutes</span>
            </div>
            <div className="info-item">
              <Star className="info-icon" />
              <span>20 Marks Total</span>
            </div>
            <div className="info-item">
              <TrendingUp className="info-icon" />
              <span>ELO-Based Difficulty</span>
            </div>
          </div>

          <div className="action-buttons">
            <button className="primary-button" onClick={startGame}>
              <Shield /> Start Ranked Match
            </button>
            <button
              className="secondary-button"
              onClick={() => window.history.back()}
            >
              <ArrowLeft /> Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading Screen
  if (gameState === "loading") {
    const currentTier = getTier(currentElo);
    const tierInfo = getTierInfo(currentTier);

    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="tier-indicator">
            <div style={{ color: tierInfo.color }}>{tierInfo.icon}</div>
            <h2>{tierInfo.name}</h2>
            <p>
              Tier {currentTier} • {currentElo} ELO
            </p>
          </div>
          <BookOpen className={`spinner-icon ${loading ? "spinning" : ""}`} />
          <p>
            {loading
              ? "Generating tier-appropriate questions..."
              : "Preparing your ranked match..."}
          </p>
          {error && (
            <div className="error-message">
              <AlertCircle />
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Reviewing Screen
  if (gameState === "reviewing") {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <Edit3 className="spinner-icon spinning" />
          <p>Analyzing your performance...</p>
          <p className="sub-text">
            Calculating ELO changes and marking responses
          </p>
        </div>
      </div>
    );
  }

  // Results Screen
  if (gameState === "results") {
    if (reviewMode) {
      return (
        <div className="review-screen">
          <div className="review-header">
            <button className="back-to-results" onClick={exitReview}>
              <ArrowLeft /> Back to Results
            </button>
            <h2>Answer Review</h2>
            <div className="passage-toggle">
              <button
                className={`toggle-button ${showPassage ? "active" : ""}`}
                onClick={() => setShowPassage(!showPassage)}
              >
                {showPassage ? <EyeOff /> : <Eye />}
                {showPassage ? "Hide" : "Show"} Passage
              </button>
            </div>
          </div>

          <div className="review-content">
            {showPassage && (
              <div className="passage-review">
                <h3>Reading Passage</h3>
                <div className="passage-text">{passage}</div>
              </div>
            )}

            <div className="question-review">
              <div className="review-navigation">
                {[...mcqQuestions, ...writtenQuestions].map((_, index) => (
                  <button
                    key={index}
                    className={`review-nav-button ${index === currentReviewQuestion ? "active" : ""}`}
                    onClick={() => setCurrentReviewQuestion(index)}
                  >
                    Q{index + 1}
                  </button>
                ))}
              </div>

              <div className="review-question">
                {currentReviewQuestion < mcqQuestions.length ? (
                  <McqReview
                    question={mcqQuestions[currentReviewQuestion]}
                    userAnswer={mcqAnswers[currentReviewQuestion]}
                    result={markingResults?.mcqResults?.[currentReviewQuestion]}
                    questionNumber={currentReviewQuestion + 1}
                  />
                ) : (
                  <WrittenReview
                    question={
                      writtenQuestions[
                        currentReviewQuestion - mcqQuestions.length
                      ]
                    }
                    userAnswer={
                      writtenAnswers[
                        currentReviewQuestion - mcqQuestions.length
                      ]
                    }
                    result={
                      markingResults?.writtenResults?.[
                        currentReviewQuestion - mcqQuestions.length
                      ]
                    }
                    questionNumber={currentReviewQuestion + 1}
                  />
                )}
              </div>

              <div className="review-controls">
                <button
                  onClick={() =>
                    setCurrentReviewQuestion(
                      Math.max(0, currentReviewQuestion - 1),
                    )
                  }
                  disabled={currentReviewQuestion === 0}
                  className="nav-button"
                >
                  <ChevronLeft /> Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentReviewQuestion(
                      Math.min(
                        getTotalQuestions() - 1,
                        currentReviewQuestion + 1,
                      ),
                    )
                  }
                  disabled={currentReviewQuestion === getTotalQuestions() - 1}
                  className="nav-button"
                >
                  Next <ChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const currentTier = getTier(currentElo);
    const newTier = getTier(newElo);
    const tierInfo = getTierInfo(currentTier);
    const newTierInfo = getTierInfo(newTier);

    return (
      <div className="results-screen">
        <div className="results-content">
          <div className="rank-change">
            <div className="old-rank">
              <div style={{ color: tierInfo.color }}>{tierInfo.icon}</div>
              <span>{currentElo} ELO</span>
            </div>
            <div className="elo-change">
              <TrendingUp
                className={`change-icon ${eloChange >= 0 ? "positive" : "negative"}`}
              />
              <span
                className={`change-value ${eloChange >= 0 ? "positive" : "negative"}`}
              >
                {eloChange >= 0 ? "+" : ""}
                {eloChange}
              </span>
            </div>
            <div className="new-rank">
              <div style={{ color: newTierInfo.color }}>{newTierInfo.icon}</div>
              <span>{newElo} ELO</span>
            </div>
          </div>

          <h2>Match Complete!</h2>

          <div className="final-stats">
            <div className="stat-item">
              <Star className="stat-icon" />
              <div>
                <div className="stat-label">Final Score</div>
                <div className="stat-value">
                  {markingResults?.totalScore || 0}/20
                </div>
              </div>
            </div>
            <div className="stat-item">
              <Clock className="stat-icon" />
              <div>
                <div className="stat-label">Time Used</div>
                <div className="stat-value">{formatTime(900 - timeLeft)}</div>
              </div>
            </div>
            <div className="stat-item">
              <Coins className="stat-icon" />
              <div>
                <div className="stat-label">Coins Earned</div>
                <div className="stat-value">{coinsEarned}</div>
              </div>
            </div>
          </div>

          <div className="results-summary">
            <div className="summary-item">
              <CheckCircle className="summary-icon correct" />
              <span>MCQ Correct: {markingResults?.mcqCorrect || 0}/6</span>
            </div>
            <div className="summary-item">
              <FileText className="summary-icon" />
              <span>Written Total: {markingResults?.writtenTotal || 0}/14</span>
            </div>
          </div>

          <div className="action-buttons">
            <button className="primary-button" onClick={startReview}>
              <Eye /> Review Answers
            </button>
            <button
              className="secondary-button"
              onClick={() => {
                setCurrentElo(newElo);
                setGameState("lobby");
                setMarkingResults(null);
                setReviewMode(false);
              }}
            >
              <RefreshCw /> Play Again
            </button>
            <button
              className="tertiary-button"
              onClick={() => window.history.back()}
            >
              <ArrowLeft /> Back to Menu
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main Game Screen
  return (
    <div className="ranked-game">
      <GameHeader
        currentElo={currentElo}
        tier={getTier(currentElo)}
        tierInfo={getTierInfo(getTier(currentElo))}
        timeLeft={timeLeft}
        formatTime={formatTime}
        currentQuestion={currentQuestion}
        totalQuestions={getTotalQuestions()}
        answeredCount={getAnsweredCount()}
        onBack={() => navigate("/games/comprehension")}
      />

      {error && (
        <div className="error-banner">
          <AlertCircle />
          {error}
        </div>
      )}

      <div className="game-layout">
        <div className="passage-panel">
          <div className="passage-header">
            <BookOpen className="passage-icon" />
            <h3>Reading Passage</h3>
            <button
              className="passage-toggle"
              onClick={() => setShowPassage(!showPassage)}
            >
              {showPassage ? <EyeOff /> : <Eye />}
            </button>
          </div>
          {showPassage && (
            <div className="passage-content">
              <div className="passage-text">{passage}</div>
            </div>
          )}
        </div>

        <div className="questions-panel">
          <QuestionNavigation
            mcqQuestions={mcqQuestions}
            writtenQuestions={writtenQuestions}
            currentQuestion={currentQuestion}
            mcqAnswers={mcqAnswers}
            writtenAnswers={writtenAnswers}
            onNavigate={navigateToQuestion}
          />

          <div className="question-content">
            <QuestionDisplay
              questionData={getCurrentQuestionData()}
              onMcqChange={handleMcqAnswerChange}
              onWrittenChange={handleWrittenAnswerChange}
            />
          </div>

          <div className="question-controls">
            <button
              className="nav-button"
              onClick={previousQuestion}
              disabled={currentQuestion === 0}
            >
              <ChevronLeft /> Previous
            </button>

            <button
              className="nav-button"
              onClick={nextQuestion}
              disabled={currentQuestion === getTotalQuestions() - 1}
            >
              Next <ChevronRight />
            </button>

            <button
              className="submit-button"
              onClick={() => {
                const allAnswered =
                  Object.values(mcqAnswers).filter(Boolean).length === mcqQuestions.length &&
                  Object.values(writtenAnswers).filter((a) => a?.trim()).length === writtenQuestions.length;
                if (allAnswered || timeLeft <= 0) {
                  handleSubmit(true);
                } else {
                  setShowSubmitModal(true);
                }
              }}
              disabled={loading}
            >
              {loading ? "Submitting..." : "Finish & Submit"}
            </button>
          </div>
        </div>
      {showSubmitModal && (
        <div className="submit-confirm-overlay" onClick={() => setShowSubmitModal(false)}>
          <div
            className="submit-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ranked-submit-title"
            ref={submitModalRef}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="submit-confirm-icon" aria-hidden="true"><AlertTriangle size={26} /></div>
            <h3 id="ranked-submit-title">Unanswered Questions</h3>
            <p>
              You have {
                (mcqQuestions.length - Object.values(mcqAnswers).filter(Boolean).length) +
                (writtenQuestions.length - Object.values(writtenAnswers).filter((a) => a?.trim()).length)
              } unanswered question(s). Submitting now will leave them blank.
            </p>
            <div className="submit-confirm-actions">
              <button className="submit-confirm-back" onClick={() => setShowSubmitModal(false)}>Go Back</button>
              <button className="submit-confirm-go" onClick={() => { setShowSubmitModal(false); handleSubmit(true); }}>Submit Anyway</button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

// Sub-components
const GameHeader = ({
  currentElo,
  tier,
  tierInfo,
  timeLeft,
  formatTime,
  currentQuestion,
  totalQuestions,
  answeredCount,
  onBack,
}) => (
  <header className="game-header">
    <div className="header-left">
      <div className="rank-indicator">
        <div style={{ color: tierInfo.color }}>{tierInfo.icon}</div>
        <div className="rank-info">
          <span className="rank-name">{tierInfo.name}</span>
          <span className="elo-display">{currentElo} ELO</span>
        </div>
      </div>
      <h1>Ranked Match</h1>
    </div>
    <div className="header-right">
      <div className="header-stats">
        <div className="progress-indicator">
          <span>Question {currentQuestion + 1} of {totalQuestions}</span>
          <span>({answeredCount}/{totalQuestions} answered)</span>
        </div>
        <div className={`timer ${timeLeft < 120 ? "urgent" : ""}`}>
          <Clock /> {formatTime(timeLeft)}
        </div>
        <button className="header-back-btn" onClick={onBack}>
          <ArrowLeft size={14} /> Back to Dashboard
        </button>
      </div>
    </div>
  </header>
);

const QuestionNavigation = ({
  mcqQuestions,
  writtenQuestions,
  currentQuestion,
  mcqAnswers,
  writtenAnswers,
  onNavigate,
}) => (
  <div className="question-navigation">
    <h4>Questions</h4>
    <div className="question-tabs">
      {mcqQuestions.map((_, index) => {
        const isAnswered = mcqAnswers[index];
        return (
          <button
            key={index}
            className={`question-tab ${index === currentQuestion ? "active" : ""} ${isAnswered ? "answered" : ""}`}
            onClick={() => onNavigate(index)}
          >
            <span className="question-number">{index + 1}</span>
            <span className="question-type">MCQ</span>
            <span className="question-marks">(1m)</span>
            {isAnswered && <CheckCircle className="answered-icon" />}
          </button>
        );
      })}
      {writtenQuestions.map((question, index) => {
        const isAnswered = writtenAnswers[index];
        const questionIndex = mcqQuestions.length + index;
        return (
          <button
            key={questionIndex}
            className={`question-tab ${questionIndex === currentQuestion ? "active" : ""} ${isAnswered ? "answered" : ""}`}
            onClick={() => onNavigate(questionIndex)}
          >
            <span className="question-number">{questionIndex + 1}</span>
            <span className="question-type">Written</span>
            <span className="question-marks">({question.marks}m)</span>
            {isAnswered && <CheckCircle className="answered-icon" />}
          </button>
        );
      })}
    </div>
  </div>
);

const QuestionDisplay = ({ questionData, onMcqChange, onWrittenChange }) => {
  if (questionData.type === "mcq") {
    return (
      <McqQuestion
        question={questionData.question}
        questionIndex={questionData.index}
        userAnswer={questionData.answer}
        onAnswerChange={onMcqChange}
        questionNumber={questionData.index + 1}
      />
    );
  } else {
    return (
      <WrittenQuestion
        question={questionData.question}
        questionIndex={questionData.index}
        userAnswer={questionData.answer}
        onAnswerChange={onWrittenChange}
        questionNumber={questionData.index + 7} // 6 MCQ + written index + 1
      />
    );
  }
};

const McqQuestion = ({
  question,
  questionIndex,
  userAnswer,
  onAnswerChange,
  questionNumber,
}) => (
  <div className="mcq-question">
    <div className="question-header">
      <h4>Question {questionNumber}</h4>
      <span className="marks-indicator">[1 mark]</span>
    </div>

    <div className="question-content">
      <p className="question-text">{question.question}</p>
      <div className="mcq-options">
        {Object.entries(question.options).map(([key, value]) => (
          <label key={key} className="mcq-option">
            <input
              type="radio"
              name={`mcq-${questionIndex}`}
              value={key}
              checked={userAnswer === key}
              onChange={(e) => onAnswerChange(questionIndex, e.target.value)}
            />
            <span className="option-key">{key}</span>
            <span className="option-text">{value}</span>
          </label>
        ))}
      </div>
    </div>
  </div>
);

const WrittenQuestion = ({
  question,
  questionIndex,
  userAnswer,
  onAnswerChange,
  questionNumber,
}) => (
  <div className="written-question">
    <div className="question-header">
      <h4>Question {questionNumber}</h4>
      <span className="marks-indicator">[{question.marks} marks]</span>
    </div>
    <div className="question-content">
      <p className="question-text">{question.question}</p>
      <textarea
        className="written-answer"
        value={userAnswer}
        onChange={(e) => onAnswerChange(questionIndex, e.target.value)}
        placeholder="Enter your answer here..."
        rows="6"
      />
    </div>
  </div>
);

const McqReview = ({ question, userAnswer, result, questionNumber }) => (
  <div className="mcq-review">
    <div className="question-header">
      <h4>Question {questionNumber}</h4>
      <span className="marks-indicator">[1 mark]</span>
      <span
        className={`result-indicator ${result?.correct ? "correct" : "incorrect"}`}
      >
        {result?.correct ? <CheckCircle /> : <XCircle />}
        {result?.correct ? "Correct" : "Incorrect"}
      </span>
    </div>
    <div className="question-content">
      <p className="question-text">{question.question}</p>
      <div className="mcq-options-review">
        {Object.entries(question.options).map(([key, value]) => (
          <div
            key={key}
            className={`option-review ${userAnswer === key ? "user-answer" : ""} ${question.correct === key ? "correct-answer" : ""}`}
          >
            <span className="option-key">{key}</span>
            <span className="option-text">{value}</span>
            {userAnswer === key && (
              <span className="indicator">Your answer</span>
            )}
            {question.correct === key && (
              <span className="indicator">Correct answer</span>
            )}
          </div>
        ))}
      </div>
      <div className="explanation">
        <h5>Explanation:</h5>
        <p>{result?.explanation}</p>
      </div>
    </div>
  </div>
);

const WrittenReview = ({ question, userAnswer, result, questionNumber }) => (
  <div className="written-review">
    <div className="question-header">
      <h4>Question {questionNumber}</h4>
      <span className="marks-indicator">[{question.marks} marks]</span>
      <span className="result-indicator">
        <Star /> {result?.marksAwarded || 0}/{question.marks} marks
      </span>
    </div>
    <div className="question-content">
      <p className="question-text">{question.question}</p>
      <div className="user-answer-display">
        <h5>Your Answer:</h5>
        <div className="answer-text">{userAnswer || "No answer provided"}</div>
      </div>
      <div className="marking-breakdown">
        <h5>Marking:</h5>
        <p>{result?.explanation}</p>
      </div>
      <div className="feedback">
        <h5>Feedback:</h5>
        <p>{result?.feedback}</p>
      </div>
    </div>
  </div>
);

export default RankedGame;
